// ⚠️ GENERATED FILE — 請勿直接編輯。
// 來源：api/src/contracts/
// 修改方式：改動來源檔後執行 `cd api && npm run sync:contracts`
import { z } from 'zod'
import { captchaAnswerSchema } from './captcha.contract.js'

/**
 * 認證相關的輸入/輸出契約。
 *
 * 這是驗證規則的**唯一**來源：後端用它擋請求，前端用同一份做即時表單驗證。
 * T1 的傷口是前端 `utils/regex_list.ts` 一套、後端另一套，兩邊會漂移。
 */

/**
 * 帳號。
 *
 * 原本沿用 T1 的規則「肯亞手機號 10 碼數字」，現在改成**獨立帳號**：
 * 6–20 碼英數。手機號當帳號的問題是它會變（換號就登不進來），
 * 而且等於把一個對外的聯絡方式當成登入憑證公開在畫面上。
 *
 * **只收大寫**，但輸入時會自動轉大寫（見 `usernameSchema`）而不是報錯。
 * 大寫是因為帳號會原樣當成遊戲商那邊的玩家 ID，而好幾家遊戲商只收大寫。
 * 允許大小寫並存的話，`Ming01` 與 `MING01` 會是兩個不同帳號 ——
 * 使用者記不得自己當初打了哪個，客服就會開始收到「我明明有註冊」的工單。
 */
export const USERNAME_MIN_LENGTH = 6
export const USERNAME_MAX_LENGTH = 20
export const USERNAME_PATTERN = /^[A-Z0-9]{6,20}$/

/** 至少 8 碼，需含大寫、小寫、數字。沿用 T1 `RegisterPasswordRegex` */
export const PASSWORD_PATTERN = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d]{8,}$/

/** `YYYY-MM-DD`。只驗格式，日期是否真實存在由 `birthdaySchema` 再檢查 */
export const BIRTHDAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/

/** 本站是博彩服務，未成年不得註冊 */
export const MIN_AGE_YEARS = 18

export const NICKNAME_MAX_LENGTH = 20

/**
 * 先 trim 再轉大寫，最後才驗格式。
 *
 * 順序是有意義的：`.toUpperCase()` 放在 regex **之前**，
 * 使用者打 `Ming01` 會被接受並存成 `MING01`，而不是被擋下來
 * 說「不可以有小寫」—— 那是把實作細節丟給使用者處理。
 *
 * 同一份 schema 也用在登入，所以大小寫在兩邊自動一致，
 * 資料庫的唯一鍵不需要額外做 case-insensitive 處理。
 */
export const usernameSchema = z
  .string()
  .trim()
  .toUpperCase()
  .pipe(z.string().regex(USERNAME_PATTERN, { message: 'USERNAME_FORMAT' }))

/** 註冊用：完整強度規則 */
export const newPasswordSchema = z.string().regex(PASSWORD_PATTERN, { message: 'PASSWORD_FORMAT' })

/**
 * 登入用：只檢查長度下限。
 * 登入時套完整強度規則沒有意義 —— 規則變更後舊密碼會被自己的驗證擋在門外。
 */
export const loginPasswordSchema = z.string().min(8, { message: 'PASSWORD_FORMAT' })

// ── 個人資料欄位 ────────────────────────────────────────
//
// 分兩類，差別是**出款時用不用得到**：
//
// - **必填**：真實姓名、手機號。出款要對得上收款人，客服要聯絡得上人。
//   這兩個欄位加上取款密碼，是出款的三個前提（見 `isWithdrawalReady`）
// - **選填**：暱稱、Email、生日。可以清空 —— 表單送出空字串代表
//   「刪掉這個值」，因此統一先 trim，空字串轉成 `null`，而不是把 `""`
//   存進資料庫：同時存在 `NULL` 和 `""` 兩種「沒填」會讓之後每個查詢都判斷兩次。
//
// 必填欄位在資料庫仍然可為 `null` —— 這兩欄是後來才加的，既有帳號本來就沒有。
// 「未填」不是壞資料，只是還不能出款；下次存個人資料時會被要求補上。

/** 空字串 → null。用在所有選填的個人資料欄位 */
const emptyToNull = (value: unknown) =>
  typeof value === 'string' && value.trim() === '' ? null : value

// ── 必填：真實姓名 ──────────────────────────────────────

export const REAL_NAME_MIN_LENGTH = 2
export const REAL_NAME_MAX_LENGTH = 40

/**
 * 字母、空白、`'`、`-`、`.`，開頭必須是字母。
 *
 * 用 `\p{L}` 而不是 `[A-Za-z]`：這個站服務的是肯亞，姓名是拉丁字母，
 * 但沒有理由把帶變音符號的名字（`Wanjirũ`）擋在外面。
 *
 * **不收數字**。姓名欄位填成帳號或手機號是最常見的填錯方式，
 * 而那會讓出款對不上收款人 —— 那是一筆錢，不是一個顯示名稱。
 */
export const REAL_NAME_PATTERN = /^\p{L}[\p{L}\s'’.-]{1,39}$/u

/**
 * 連續空白收斂成一個。`Mary   Wanjiru` 與 `Mary Wanjiru` 是同一個人，
 * 存成兩種寫法只會讓之後的人工核對多一道無意義的差異。
 */
export const realNameSchema = z
  .string()
  .trim()
  .transform((value) => value.replaceAll(/\s+/gu, ' '))
  .pipe(z.string().regex(REAL_NAME_PATTERN, { message: 'REAL_NAME_FORMAT' }))

// ── 必填：手機號 ────────────────────────────────────────

/**
 * 中國大陸手機號，11 碼、`1` 開頭、第 2 碼是 `3`～`9`。
 *
 * 與 `wallet.contract.ts` 的 `EWALLET_ACCOUNT_PATTERN` 目前是同一條規則，
 * 但**刻意各存一份**：這裡是「聯絡得到本人的號碼」，那裡是「錢要匯去的號碼」。
 * 兩者可以是不同的號碼，將來也可能各自改（例如收款開放企業號）——
 * 合成一個常數的話，改其中一邊就會靜靜地連動另一邊。
 */
export const PHONE_PATTERN = /^1[3-9]\d{9}$/

/**
 * 輸入正規化：去掉空白與 `-`，把國碼寫法折回本地寫法。
 *
 * `+8613812345678`、`8613812345678`、`138 1234 5678` 是同一個號碼。
 * 不折的話資料庫裡會有三種寫法，而「這個號碼是不是已經有人用了」
 * 這種比對就永遠對不齊。
 */
const normalizePhone = (value: unknown) => {
  if (typeof value !== 'string') return value

  const compact = value.replaceAll(/[\s-]/gu, '')
  if (compact.startsWith('+86')) return compact.slice(3)
  if (compact.startsWith('86')) return compact.slice(2)
  return compact
}

export const phoneSchema = z.preprocess(
  normalizePhone,
  z.string().regex(PHONE_PATTERN, { message: 'PHONE_FORMAT' }),
)

export const nicknameSchema = z.preprocess(
  emptyToNull,
  z
    .string()
    .trim()
    .min(1, { message: 'NICKNAME_FORMAT' })
    .max(NICKNAME_MAX_LENGTH, { message: 'NICKNAME_TOO_LONG' })
    .nullable(),
)

export const emailSchema = z.preprocess(
  emptyToNull,
  z
    .string()
    .trim()
    .toLowerCase()
    .pipe(z.email({ message: 'EMAIL_FORMAT' }))
    .nullable(),
)

/**
 * 生日。三道檢查缺一不可：
 * 1. 格式是 `YYYY-MM-DD`
 * 2. 真的是存在的日期 —— 光靠 regex 會放行 `2026-02-31`
 * 3. 年滿 18 且不是未來日期
 *
 * 用 UTC 比較，不看伺服器時區：生日是日曆日期，
 * 換一台時區不同的機器不該讓同一個人忽然變成未成年。
 */
export const birthdaySchema = z.preprocess(
  emptyToNull,
  z
    .string()
    .trim()
    .regex(BIRTHDAY_PATTERN, { message: 'BIRTHDAY_FORMAT' })
    .refine(isRealDate, { message: 'BIRTHDAY_FORMAT' })
    .refine((value) => !isFutureDate(value), { message: 'BIRTHDAY_FUTURE' })
    .refine((value) => ageOn(value) >= MIN_AGE_YEARS, { message: 'BIRTHDAY_UNDERAGE' })
    .nullable(),
)

function isRealDate(value: string): boolean {
  const parsed = new Date(`${value}T00:00:00Z`)
  // 反向格式化再比對，才擋得住 `2026-02-31` 這種會被自動進位成 3/3 的日期
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value
}

function isFutureDate(value: string): boolean {
  return value > new Date().toISOString().slice(0, 10)
}

/** 以今天（UTC）為基準的足歲 */
export function ageOn(birthday: string, today = new Date()): number {
  const [year, month, day] = birthday.split('-').map(Number)
  let age = today.getUTCFullYear() - (year ?? 0)

  const monthDiff = today.getUTCMonth() + 1 - (month ?? 0)
  // 生日還沒到就扣一歲
  if (monthDiff < 0 || (monthDiff === 0 && today.getUTCDate() < (day ?? 0))) age -= 1

  return age
}

// ── 請求 ────────────────────────────────────────────────

/**
 * 註冊用的帳號：再多擋含 `TEST` 的。
 *
 * 只擋註冊不擋登入 —— 已經存在的 `GARYTEST` 這類帳號照樣登得進去，
 * 擋登入等於把人鎖在自己的帳號外面。
 */
export const registerUsernameSchema = usernameSchema.refine((u) => !u.includes('TEST'), {
  message: 'USERNAME_RESERVED',
})

export const registerRequestSchema = z.object({
  username: registerUsernameSchema,
  password: newPasswordSchema,
})
export type RegisterRequest = z.infer<typeof registerRequestSchema>

/**
 * 登入。
 *
 * 帶圖形驗證碼，註冊則不帶 —— 註冊本來就會建立資料，
 * 濫用的防線是速率限制與手機號唯一性；登入才是被暴力嘗試的目標。
 */
export const loginRequestSchema = z.object({
  username: usernameSchema,
  password: loginPasswordSchema,
  captchaId: z.string().min(1, { message: 'CAPTCHA_REQUIRED' }),
  captchaAnswer: captchaAnswerSchema,
})
export type LoginRequest = z.infer<typeof loginRequestSchema>

/**
 * 更新個人資料。
 *
 * 兩種欄位混在同一份表單裡，規則不同：
 *
 * - `realName` / `phone`：**不能為空**。出款要對得上收款人、客服要找得到人，
 *   而使用者一次填完比出款當下被打斷去補資料好
 * - 其餘三個是**必送但可為 null**（不是 optional）：表單一次送出完整狀態，
 *   `null` 明確代表「清空」。做成 optional 的話，「沒送這個欄位」與
 *   「要把它清掉」就分不出來
 *
 * 既有帳號那兩欄是 `null`，所以第一次打開這個表單會被要求補齊。
 * 這是刻意的 —— 那兩個欄位缺著，人就領不到錢。
 */
export const updateProfileRequestSchema = z.object({
  realName: realNameSchema,
  phone: phoneSchema,
  nickname: nicknameSchema,
  email: emailSchema,
  birthday: birthdaySchema,
})
export type UpdateProfileRequest = z.infer<typeof updateProfileRequestSchema>

/**
 * 修改密碼。
 *
 * `currentPassword` 用寬鬆規則（同登入）—— 舊密碼可能是強度規則變更前設的，
 * 用新規則驗會把使用者擋在自己的帳號外面。
 * `newPassword` 才套完整強度規則。
 */
export const changePasswordRequestSchema = z
  .object({
    currentPassword: loginPasswordSchema,
    newPassword: newPasswordSchema,
  })
  .refine((input) => input.currentPassword !== input.newPassword, {
    message: 'PASSWORD_UNCHANGED',
    path: ['newPassword'],
  })
export type ChangePasswordRequest = z.infer<typeof changePasswordRequestSchema>

// ── 取款密碼 ────────────────────────────────────────────
//
// 提交出款時要輸入的 4 位數字。與登入密碼分開是刻意的：
// 就算登入憑證外洩，對方也還沒辦法把錢領走。

export const WITHDRAWAL_PASSWORD_LENGTH = 4
export const WITHDRAWAL_PASSWORD_PATTERN = /^\d{4}$/

/** 連續錯幾次就鎖住。4 位數只有 10000 組，不鎖就等於沒設 */
export const WITHDRAWAL_MAX_ATTEMPTS = 5
export const WITHDRAWAL_LOCK_SECONDS = 15 * 60

export const withdrawalPasswordSchema = z
  .string()
  .trim()
  .regex(WITHDRAWAL_PASSWORD_PATTERN, { message: 'WITHDRAWAL_PASSWORD_FORMAT' })

/**
 * 設定或修改取款密碼。
 *
 * 一律要求輸入**登入密碼**，即使是第一次設定 ——
 * 別人搶到一個沒鎖的瀏覽器分頁就不能默默把出款密碼設成自己的。
 * 不要求「舊的取款密碼」：登入密碼已經足以證明身分，
 * 多問一道只會讓忘記取款密碼的人卡死。
 */
export const setWithdrawalPasswordRequestSchema = z.object({
  loginPassword: loginPasswordSchema,
  withdrawalPassword: withdrawalPasswordSchema,
})
export type SetWithdrawalPasswordRequest = z.infer<typeof setWithdrawalPasswordRequestSchema>

// ── 回應 ────────────────────────────────────────────────

export const publicUserSchema = z.object({
  id: z.string(),
  username: z.string(),
  status: z.enum(['active', 'suspended']),
  createdAt: z.number().int(),
  /**
   * 出款必備。這兩欄在**新的**資料一定有值（`updateProfileRequestSchema`
   * 不收空值），但既有帳號是在這兩欄存在之前建的，所以型別仍是 nullable。
   * 畫面靠它判斷要不要引導使用者去補（見 `isWithdrawalReady`）。
   */
  realName: z.string().nullable(),
  phone: z.string().nullable(),
  /** 以下為選填的個人資料，未填時是 `null` */
  nickname: z.string().nullable(),
  email: z.string().nullable(),
  birthday: z.string().nullable(),
  /**
   * 只回「有沒有設定」，永遠不回雜湊也不回明碼。
   * 畫面需要知道要顯示「設定」還是「修改」，僅此而已。
   */
  hasWithdrawalPassword: z.boolean(),
})
export type PublicUser = z.infer<typeof publicUserSchema>

/**
 * 出款的三個前提。
 *
 * 放在 contract 而不是各自實作：後端要用它擋請求，前端要用同一份決定
 * 「取款頁上要顯示表單還是待辦清單」。兩邊各寫一份的下場是畫面說可以領、
 * 送出去卻被打回來 —— 那比一開始就擋住更糟。
 *
 * 為什麼是這三個：
 * - **真實姓名**：出款的收款人要對得上，這是金流那一側的要求
 * - **手機號**：出款出問題時客服唯一找得到人的方式
 * - **取款密碼**：登入憑證外洩不該等於錢被領走
 */
export function isWithdrawalReady(
  user: Pick<PublicUser, 'realName' | 'phone' | 'hasWithdrawalPassword'>,
): boolean {
  return user.realName !== null && user.phone !== null && user.hasWithdrawalPassword
}

/** 還缺哪幾項。畫面照這個順序列出待辦，不用自己再判斷一次 */
export function missingWithdrawalRequirements(
  user: Pick<PublicUser, 'realName' | 'phone' | 'hasWithdrawalPassword'>,
): ('realName' | 'phone' | 'withdrawalPassword')[] {
  const missing: ('realName' | 'phone' | 'withdrawalPassword')[] = []

  if (user.realName === null) missing.push('realName')
  if (user.phone === null) missing.push('phone')
  if (!user.hasWithdrawalPassword) missing.push('withdrawalPassword')

  return missing
}

/** 改密碼成功後回報踢掉了幾條 session，前端據此提示使用者 */
export const changePasswordResponseSchema = z.object({
  revokedSessions: z.number().int().min(0),
})
export type ChangePasswordResponse = z.infer<typeof changePasswordResponseSchema>

/**
 * Access token 走回應 body，由前端存在記憶體。
 * Refresh token **不在這裡** —— 它只以 httpOnly cookie 傳遞，JS 讀不到。
 */
export const authSessionSchema = z.object({
  user: publicUserSchema,
  accessToken: z.string(),
  /** access token 剩餘秒數，前端據此決定何時提前 refresh */
  expiresIn: z.number().int().positive(),
})
export type AuthSession = z.infer<typeof authSessionSchema>

export const meResponseSchema = z.object({ user: publicUserSchema })
export type MeResponse = z.infer<typeof meResponseSchema>

/**
 * Access token 的 JWT payload。
 *
 * `sid` 是站台 id，**不能省**。這是一個白標平台，同一個部署會服務多個站，
 * 而 `sub`（使用者 id）是全域唯一的 —— 沒有 `sid` 的話，在 A 站拿到的 token
 * 帶去打 B 站的 API 一樣會查到同一個使用者，等於跨站接管。
 *
 * 驗證在 `plugins/auth.ts`：`sid` 與這次請求解析出來的站台不符時，
 * 這個 token 直接當作沒帶。
 */
export const accessTokenClaimsSchema = z.object({
  sub: z.string(),
  username: z.string(),
  sid: z.string(),
})
export type AccessTokenClaims = z.infer<typeof accessTokenClaimsSchema>
